import { useEffect, useState, useCallback, useRef } from 'react';
import { chatService, ChatEventHandlers } from '../../services/chatService';
import * as api from '../../services/apiClient';
import { logger } from '../../utils/logger';
import { useStreamingContext } from '../../context/StreamingContext';

// Helper function for findLastIndex (not available in ES2021)
function findLastIndex<T>(arr: T[], predicate: (item: T) => boolean): number {
  for (let i = arr.length - 1; i >= 0; i--) {
    if (predicate(arr[i])) return i;
  }
  return -1;
}

interface UseChatOptions {
  conversationId?: string;
}

export interface ToolCall {
  id: string;
  name: string;
  /** Display name for UI (e.g., "Lecture de `document.docx`") */
  displayName?: string;
  args: Record<string, unknown>;
  status: 'running' | 'success' | 'error';
  result?: string;
}

/** A segment in the stream - either text or a tool call, stored in arrival order */
export interface StreamSegment {
  type: 'text' | 'tool';
  /** For text segments: the accumulated text content */
  text?: string;
  /** For tool segments: reference to the tool */
  toolId?: string;
}

/** Generated contract data from contract creation flow */
export interface GeneratedContract {
  content_b64: string;
  filename: string;
  media_type: string;
  size_bytes: number;
}

/** Info about an attached file for display purposes */
export interface AttachedFileInfo {
  name: string;
  size: number;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'bot';
  content: string;
  timestamp?: string;
  isStreaming?: boolean;
  toolCalls?: ToolCall[];
  /** Stream segments in chronological arrival order */
  streamSegments?: StreamSegment[];
  /** Generated contract data (only present when a contract was generated) */
  generatedContract?: GeneratedContract | null;
  /** Attached files (for user messages) */
  attachedFiles?: AttachedFileInfo[];
}

interface ChatStage {
  stage: string;
  message?: string;
  timestamp?: string;
}

interface ConnectionState {
  status: 'connected' | 'disconnected' | 'connecting' | 'streaming' | 'error';
  error?: string;
  reconnectAttempts?: number;
}

/**
 * Custom hook to manage chat functionality for web (using chatService)
 * Handles message sending, streaming reception, and connection state
 */
export function useChat({ conversationId: initialConversationId }: UseChatOptions = {}) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [isWaitingFirstEvent, setIsWaitingFirstEvent] = useState(false);
  const [connectionState, setConnectionState] = useState<ConnectionState>({
    status: 'disconnected'
  });
  const [conversationId, setConversationId] = useState<string | null>(
    initialConversationId ?? null
  );
  const [conversationName, setConversationName] = useState<string | null>(null);
  const [currentStage, setCurrentStage] = useState<ChatStage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [thinkingMessage, setThinkingMessage] = useState<string | null>(null);

  // Global streaming state to coordinate with other contexts (e.g., pause notification polling)
  const { setStreamingActive, setStreamingInactive } = useStreamingContext();

  // Keep track of current streaming message
  const streamingMessageRef = useRef<string>('');
  const streamingMessageIdRef = useRef<string>('');

  // Stream segments in chronological order (text chunks and tools as they arrive)
  const streamSegmentsRef = useRef<StreamSegment[]>([]);

  // Keep track of latest conversationId to avoid stale closures
  const conversationIdRef = useRef<string | null>(conversationId);

  // Refs for streaming state functions to avoid stale closures in handlers
  const setStreamingInactiveRef = useRef(setStreamingInactive);

  // Ref to track the timeout for clearing streaming refs after error (for cleanup/de-dupe)
  const clearStreamingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync refs with current values
  useEffect(() => {
    conversationIdRef.current = conversationId;
  }, [conversationId]);

  useEffect(() => {
    setStreamingInactiveRef.current = setStreamingInactive;
  }, [setStreamingInactive]);

  // Set up chat service event handlers
  useEffect(() => {
    const handlers: ChatEventHandlers = {
      // Conversation started handler (for new conversations)
      onConversationStarted: (data) => {
        logger.log('[useChat] NEW CONVERSATION STARTED', {
          conversationId: data.conversationId,
          conversationName: data.conversationName
        });

        // Update conversation ID and name
        conversationIdRef.current = data.conversationId;
        setConversationId(data.conversationId);
        if (data.conversationName) {
          setConversationName(data.conversationName);
        }

        // Bind orphan messages to the new conversationId
        if (streamingMessageIdRef.current) {
          setMessages((prev) => {
            const streamingIdx = prev.findIndex((msg) => msg.id === streamingMessageIdRef.current);
            if (streamingIdx === -1) return prev;

            const updated = [...prev];
            if (streamingIdx > 0) {
              updated[streamingIdx - 1] = { ...updated[streamingIdx - 1] };
            }
            updated[streamingIdx] = { ...updated[streamingIdx] };
            return updated;
          });
        }
      },

      // Conversation updated handler (title change from backend)
      onConversationUpdated: (data) => {
        const latestConversationId = conversationIdRef.current;

        logger.log('[useChat] CONVERSATION UPDATED', {
          conversationId: data.conversationId,
          name: data.name
        });

        if (data.conversationId === latestConversationId) {
          setConversationName(data.name);
        }
      },

      // Thinking/status message handler
      onThinking: (data) => {
        const latestConversationId = conversationIdRef.current;
        const isMatchingConversation =
          !latestConversationId ||
          !data.conversationId ||
          data.conversationId === latestConversationId;

        if (!isMatchingConversation) return;

        logger.log('[useChat] THINKING', { content: data.content });
        setThinkingMessage(data.content);
      },

      // Token streaming handler
      onToken: (data) => {
        const latestConversationId = conversationIdRef.current;
        const isActiveStreaming = !!streamingMessageIdRef.current;
        const isMatchingConversation =
          !latestConversationId ||
          !data.conversationId ||
          data.conversationId === latestConversationId;

        if (!isActiveStreaming || !isMatchingConversation) {
          return;
        }

        // First event received - stop waiting indicator and clear thinking message
        setIsWaitingFirstEvent(false);
        setThinkingMessage(null);

        streamingMessageRef.current += data.token;

        // Update stream segments
        const segments = streamSegmentsRef.current;
        const lastSegment = segments[segments.length - 1];

        if (lastSegment && lastSegment.type === 'text') {
          lastSegment.text = (lastSegment.text || '') + data.token;
        } else {
          segments.push({ type: 'text', text: data.token });
        }

        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === streamingMessageIdRef.current
              ? {
                  ...msg,
                  content: streamingMessageRef.current,
                  isStreaming: true,
                  streamSegments: [...streamSegmentsRef.current]
                }
              : msg
          )
        );
      },

      // Tool start handler
      onToolStart: (data) => {
        logger.log('[useChat] TOOL START', { tool: data.toolName });

        if (!streamingMessageIdRef.current) return;

        // First event received - stop waiting indicator and clear thinking message
        setIsWaitingFirstEvent(false);
        setThinkingMessage(null);

        const toolId = `tool-${Date.now()}`;
        const newToolCall: ToolCall = {
          id: toolId,
          name: data.toolName,
          displayName: data.toolDisplayName,
          args: data.args || {},
          status: 'running'
        };

        streamSegmentsRef.current.push({ type: 'tool', toolId });

        setMessages((prev) =>
          prev.map((msg) => {
            if (msg.id !== streamingMessageIdRef.current) return msg;

            const existingTools = msg.toolCalls || [];
            return {
              ...msg,
              toolCalls: [...existingTools, newToolCall],
              streamSegments: [...streamSegmentsRef.current]
            };
          })
        );
      },

      // Tool end handler
      onToolEnd: (data) => {
        logger.log('[useChat] TOOL END', { resultPreview: data.resultPreview });

        if (!streamingMessageIdRef.current) return;

        setMessages((prev) =>
          prev.map((msg) => {
            if (msg.id !== streamingMessageIdRef.current) return msg;
            if (!msg.toolCalls || msg.toolCalls.length === 0) return msg;

            const updatedTools = [...msg.toolCalls];
            const lastRunningIdx = findLastIndex(updatedTools, (t: ToolCall) => t.status === 'running');

            if (lastRunningIdx !== -1) {
              updatedTools[lastRunningIdx] = {
                ...updatedTools[lastRunningIdx],
                status: 'success',
                result: data.resultPreview || ''
              };
            }

            return { ...msg, toolCalls: updatedTools };
          })
        );
      },

      // Final message handler
      onFinal: (data) => {
        const latestConversationId = conversationIdRef.current;
        // Capture ref value immediately to avoid race conditions on unstable networks (5G)
        const currentStreamingId = streamingMessageIdRef.current;

        logger.log('[useChat] FINAL MESSAGE', data);

        const isMatchingConversation = latestConversationId && data.conversationId === latestConversationId;
        const isActiveStreaming = currentStreamingId && (
          !latestConversationId || !data.conversationId || data.conversationId === latestConversationId
        );
        // Allow FINAL when no conversation context exists on either side
        const isNoActiveConversation = !data.conversationId && !latestConversationId;

        // Strict check: only accept FINAL when conversation matches, streaming is active for same conversation,
        // or no conversation context exists — reject FINAL events targeting a different conversation
        if (!isMatchingConversation && !isActiveStreaming && !isNoActiveConversation) {
          return;
        }

        const streamedContent = streamingMessageRef.current;
        const finalContent = data.content || '';
        const contentToUse = finalContent.length >= streamedContent.length ? finalContent : streamedContent;

        setMessages((prev) => {
          // Fallback: if ref was cleared (e.g., by error handler on network issue),
          // find the last assistant message that's streaming OR has a temporary error
          let targetId = currentStreamingId;

          if (!targetId) {
            // Search from end to find the most recent candidate
            for (let i = prev.length - 1; i >= 0; i--) {
              const msg = prev[i];
              if (msg.role === 'bot' && (
                msg.isStreaming ||
                msg.content?.startsWith('Error:') ||
                msg.id?.startsWith('temp-')
              )) {
                targetId = msg.id;
                break;
              }
            }
          }

          if (!targetId) {
            logger.warn('[useChat] FINAL MESSAGE: No target message found, content lost:', contentToUse.length);
            return prev;
          }

          return prev.map((msg) =>
            msg.id === targetId
              ? {
                  ...msg,
                  id: data.messageId || msg.id,
                  content: contentToUse,
                  timestamp: data.timestamp,
                  isStreaming: false,
                  generatedContract: data.generatedContract || null
                }
              : msg
          );
        });

        setIsSending(false);
        setIsWaitingFirstEvent(false);
        setCurrentStage(null);
        setThinkingMessage(null);

        // Signal streaming end to resume notification polling
        setStreamingInactiveRef.current();

        streamingMessageRef.current = '';
        streamingMessageIdRef.current = '';
      },

      // Error handler
      onError: (data) => {
        const latestConversationId = conversationIdRef.current;

        if (data.conversationId && data.conversationId !== latestConversationId) {
          return;
        }

        logger.error('Chat error:', data.error);
        setError(data.error);
        setIsSending(false);
        setIsWaitingFirstEvent(false);
        setCurrentStage(null);
        setThinkingMessage(null);

        // Signal streaming end to resume notification polling
        setStreamingInactiveRef.current();

        if (streamingMessageIdRef.current) {
          const errorMessageId = streamingMessageIdRef.current;
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === errorMessageId
                ? { ...msg, content: `Error: ${data.error}`, isStreaming: false }
                : msg
            )
          );
          // Clear any existing timeout to de-dupe before setting a new one
          if (clearStreamingTimeoutRef.current) {
            clearTimeout(clearStreamingTimeoutRef.current);
          }
          // Delay ref clearing to allow buffered final events to arrive on unstable networks (5G)
          // The final event might be in-flight when the error occurred
          clearStreamingTimeoutRef.current = setTimeout(() => {
            // Only clear if still pointing to the same message (not a new stream)
            if (streamingMessageIdRef.current === errorMessageId) {
              streamingMessageRef.current = '';
              streamingMessageIdRef.current = '';
            }
            clearStreamingTimeoutRef.current = null;
          }, 1000);
        }
      },

      // Connection status handler
      onConnectionStatus: (state) => {
        setConnectionState({
          status: state.status as ConnectionState['status'],
          error: state.error
        });
      },

      // Tool confirmation handler - Web version doesn't support local tool execution
      // Tools that require local execution (like Word editing) are not available in web
      onToolConfirmation: async (data) => {
        logger.warn(
          'Tool confirmation received but local execution not supported in web:',
          data.toolName
        );

        // Send failure response since we can't execute local tools
        await chatService.sendToolResponse({
          executionId: data.executionId,
          success: false,
          error: 'Local tool execution not supported in web version'
        });
      },

      // Stream resumed handler
      onStreamResumed: (data) => {
        logger.log('Stream resumed with tool result:', data.toolResult);
      },

      // Cancelled handler
      onCancelled: (data) => {
        logger.log('[useChat] GENERATION CANCELLED', data);

        setIsSending(false);
        setIsWaitingFirstEvent(false);
        setCurrentStage(null);
        setThinkingMessage(null);

        // Signal streaming end to resume notification polling
        setStreamingInactiveRef.current();

        // Update the streaming message to show it was cancelled
        if (streamingMessageIdRef.current) {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === streamingMessageIdRef.current
                ? { ...msg, isStreaming: false }
                : msg
            )
          );
          streamingMessageRef.current = '';
          streamingMessageIdRef.current = '';
        }
      }
    };

    chatService.setHandlers(handlers);

    // Cleanup on unmount
    return () => {
      chatService.setHandlers({});
      // Clear any pending timeout to avoid dangling timers
      if (clearStreamingTimeoutRef.current) {
        clearTimeout(clearStreamingTimeoutRef.current);
        clearStreamingTimeoutRef.current = null;
      }
    };
  }, []);

  /**
   * Send a message to the chat
   */
  const sendMessage = useCallback(
    async (content: string, options?: {
      building_contract_id?: number;
      deep_web_search?: boolean;
      attachedFileKeys?: string[];
      attachedFileInfos?: AttachedFileInfo[];
    }): Promise<void> => {
      if (!content.trim()) {
        throw new Error('Message content cannot be empty');
      }

      setIsSending(true);
      setIsWaitingFirstEvent(true);
      setError(null);
      setThinkingMessage(null);

      // Signal streaming start to pause notification polling
      setStreamingActive();

      // Generate temporary ID for the bot message
      const tempBotMessageId = `temp-${Date.now()}`;
      streamingMessageIdRef.current = tempBotMessageId;
      streamingMessageRef.current = '';
      streamSegmentsRef.current = [];

      // Add user message
      const userMessage: ChatMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: content.trim(),
        timestamp: new Date().toISOString(),
        attachedFiles: options?.attachedFileInfos
      };

      // Add empty bot message for streaming
      const botMessage: ChatMessage = {
        id: tempBotMessageId,
        role: 'bot',
        content: '',
        isStreaming: true,
        streamSegments: []
      };

      setMessages((prev) => [...prev, userMessage, botMessage]);

      try {
        // Use ref for most up-to-date conversationId (updated synchronously from SSE events)
        const currentConversationId = conversationIdRef.current;

        // Subscribe to WebSocket if we have a conversation
        if (currentConversationId) {
          await chatService.subscribe(currentConversationId);
        }

        logger.log('[useChat] Sending message with conversationId:', currentConversationId);

        // Send message via chat service
        const result = await chatService.send({
          conversationId: currentConversationId ?? undefined,
          content: content.trim(),
          building_contract_id: options?.building_contract_id,
          deep_web_search: options?.deep_web_search,
          attached_file_keys: options?.attachedFileKeys
        });

        if (!result.success) {
          throw new Error(result.error || 'Failed to send message');
        }

        // Subscribe to the new conversation if one was created
        if (result.conversationId && result.conversationId !== currentConversationId) {
          await chatService.subscribe(result.conversationId);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        logger.error('Error sending message:', errorMessage);
        setError(errorMessage);
        setIsSending(false);
        setIsWaitingFirstEvent(false);

        // Signal streaming end to resume notification polling
        setStreamingInactive();

        // Remove the bot message on error
        setMessages((prev) => prev.filter((msg) => msg.id !== tempBotMessageId));
        streamingMessageRef.current = '';
        streamingMessageIdRef.current = '';

        throw err;
      }
    },
    [setStreamingActive, setStreamingInactive]
  );

  /**
   * Clear all messages
   */
  const clearMessages = useCallback(() => {
    setMessages([]);
    setConversationId(null);
    setConversationName(null);
    setError(null);
    setIsSending(false);
    setIsWaitingFirstEvent(false);
    setThinkingMessage(null);

    // Ensure streaming state is reset
    setStreamingInactive();

    streamingMessageRef.current = '';
    streamingMessageIdRef.current = '';
    streamSegmentsRef.current = [];
    chatService.unsubscribe();
  }, [setStreamingInactive]);

  /**
   * Disconnect from chat service
   */
  const disconnect = useCallback(async () => {
    await chatService.unsubscribe();
  }, []);

  /**
   * Load an existing conversation
   */
  const loadConversation = useCallback(
    async (id: string, name: string) => {
      try {
        // Clear current messages and state
        setMessages([]);
        setError(null);
        setIsSending(false);
        streamingMessageRef.current = '';
        streamingMessageIdRef.current = '';
        streamSegmentsRef.current = [];

        // Set new conversation
        setConversationId(id);
        setConversationName(name);

        // Subscribe to WebSocket
        await chatService.subscribe(id);

        // Fetch messages from backend
        const response = await api.getConversationMessages(id);

        if (!response.success || !response.data) {
          throw new Error(response.error || 'Failed to load messages');
        }

        // Transform backend messages to ChatMessage format
        const historicalMessages: ChatMessage[] = response.data.items.map((msg) => ({
          id: msg.id,
          role: msg.role === 'user' ? 'user' : 'bot',
          content: msg.content,
          timestamp: msg.timestamp,
          isStreaming: false,
          toolCalls: msg.tool_calls?.map((tc) => ({
            id: tc.id,
            name: tc.name,
            displayName: tc.display_name,
            args: tc.args,
            status: tc.status,
            result: tc.result
          })),
          attachedFiles: msg.attached_files?.map((af) => ({
            name: af.name,
            size: af.size
          })),
          generatedContract: msg.generated_contract
            ? {
                content_b64: msg.generated_contract.content_b64,
                filename: msg.generated_contract.filename,
                media_type: msg.generated_contract.media_type,
                size_bytes: msg.generated_contract.size_bytes
              }
            : undefined
        }));

        setMessages(historicalMessages);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load conversation';
        logger.error('Error loading conversation:', errorMessage);
        setError(errorMessage);
      }
    },
    []
  );

  /**
   * Cancel ongoing generation
   */
  const cancelGeneration = useCallback(async (): Promise<void> => {
    const currentConversationId = conversationIdRef.current;
    if (!currentConversationId || !isSending) return;

    try {
      const response = await api.cancelGeneration(currentConversationId);
      logger.log('[useChat] Cancel response:', response);
      // The actual cleanup happens via the onCancelled handler when SSE receives the cancelled event
    } catch (err) {
      logger.error('Error cancelling generation:', err);

      // Perform local cleanup if API call fails, so the UI isn't stuck
      // This mirrors the onCancelled SSE handler logic
      setIsSending(false);
      setIsWaitingFirstEvent(false);
      setCurrentStage(null);

      // Signal streaming end to resume notification polling
      setStreamingInactiveRef.current();

      // Update the streaming message to show it was cancelled
      if (streamingMessageIdRef.current) {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === streamingMessageIdRef.current
              ? { ...msg, isStreaming: false }
              : msg
          )
        );
        streamingMessageRef.current = '';
        streamingMessageIdRef.current = '';
        streamSegmentsRef.current = [];
      }
    }
  }, [isSending]);

  return {
    messages,
    isSending,
    isWaitingFirstEvent,
    thinkingMessage,
    connectionState,
    conversationId,
    conversationName,
    currentStage,
    error,
    setError,
    sendMessage,
    clearMessages,
    disconnect,
    loadConversation,
    cancelGeneration
  };
}
